import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { HsePageChrome } from "../HsePageChrome";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { ResourceError } from "../../../utils/ResourceError";
import { EmployeePickerSheet, avatarColor, initialsOf } from "../../hours/hourFormPickers";
import { getUsersAsync } from "../../../../slices/user";
import {
  createEvaluation,
  evaluationApiError,
  fetchEvaluation,
  updateEvaluation,
} from "../../../../api/hse/evaluations";
import { EvaluationLabelInfo } from "./EvaluationLabelInfo";
import {
  EVALUATION_RESULTS,
  EVALUATION_STATUSES,
  EVALUATION_TYPES,
  evaluationPath,
  personLabel,
} from "./evaluationUtils";

const inputClass =
  "w-full rounded-[14px] border-[1.5px] border-slate-200 bg-white px-4 py-3 text-[15px] text-slate-900 outline-none focus:border-[#1463C7]";

const emptyForm = {
  title: "",
  status: "draft",
  result: "",
  scheduled_on: "",
  held_on: "",
  period_from: "",
  period_to: "",
  next_review_on: "",
  scope: "",
  summary: "",
  findings: "",
  conclusions: "",
  responsible_user_id: "",
};

export function EvaluationForm({ evaluationType, id }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isNew = !id;
  const isAudit = evaluationType === EVALUATION_TYPES.INTERNAL_AUDIT;
  const users = useSelector((state) => state.users?.data || []);

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [leadPickerOpen, setLeadPickerOpen] = useState(false);

  useEffect(() => {
    dispatch(getUsersAsync({ filters: [{ name: "EMPLOYEE", value: true }] }));
  }, [dispatch]);

  useEffect(() => {
    if (isNew) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError("");
        const record = await fetchEvaluation(id, evaluationType);
        if (cancelled) return;
        if (!record) {
          setError(t("hse.evaluation.view.not_found"));
          return;
        }
        setForm({
          title: record.title,
          status: record.status,
          result: record.result || "",
          scheduled_on: record.scheduled_on,
          held_on: record.held_on,
          period_from: record.period_from,
          period_to: record.period_to,
          next_review_on: record.next_review_on,
          scope: record.scope,
          summary: record.summary,
          findings: record.findings,
          conclusions: record.conclusions,
          responsible_user_id: record.responsible_user_id || "",
        });
      } catch (err) {
        if (!cancelled) setError(evaluationApiError(err) || t("hse.evaluation.list.load_failed"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isNew, evaluationType, t]);

  const lead = users.find((user) => String(user.id) === String(form.responsible_user_id));
  const listPath = evaluationPath({ kind: evaluationType });
  const setField = (name, value) => setForm((current) => ({ ...current, [name]: value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) {
      toast.error(t("hse.evaluation.form.title_required"));
      return;
    }
    try {
      setSaving(true);
      const saved = isNew
        ? await createEvaluation(form, evaluationType)
        : await updateEvaluation(id, form, evaluationType);
      toast.success(t("hse.evaluation.form.saved"));
      navigate(evaluationPath(saved || { id, kind: evaluationType }));
    } catch (err) {
      toast.error(evaluationApiError(err) || t("hse.evaluation.form.save_failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <HsePageChrome
      title={
        isNew
          ? t(isAudit ? "hse.evaluation.form.new_audit" : "hse.evaluation.form.new_review")
          : t(isAudit ? "hse.evaluation.form.edit_audit" : "hse.evaluation.form.edit_review")
      }
      subtitle={t(isAudit ? "hse.evaluation.nav.audit_hint" : "hse.evaluation.nav.review_hint")}
      backTo={isNew ? evaluationPath({ kind: evaluationType }) : evaluationPath({ kind: evaluationType, id })}
      backButton={{
        label: t("hse.evaluation.back"),
        action: () => navigate(id ? evaluationPath({ id, kind: evaluationType }) : listPath),
        icon: ArrowLeftIcon,
      }}
    >
      <div className="mx-auto mt-8 max-w-4xl px-4 sm:px-6 lg:px-8">
        {loading ? (
          <ResourceLoading size="base" />
        ) : error ? (
          <ResourceError errors={[error]} onRetry={() => window.location.reload()} />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 rounded-2xl bg-[#F8FAFC] p-4 sm:p-6">
            <section className="rounded-2xl border border-slate-200 bg-white p-5">
              <h3 className="mb-4 text-[16px] font-extrabold text-slate-900">
                {t("hse.evaluation.form.section.basics")}
              </h3>
              <div className="grid gap-5 sm:grid-cols-2">
                <label className="sm:col-span-2">
                  <span className="mb-2 block text-[13px] font-bold text-slate-700">
                    {t("hse.evaluation.fields.title")}
                  </span>
                  <input
                    className={inputClass}
                    value={form.title}
                    onChange={(event) => setField("title", event.target.value)}
                    required
                  />
                </label>
                <div>
                  <span className="mb-2 block text-[13px] font-bold text-slate-700">
                    <EvaluationLabelInfo
                      label={t("hse.evaluation.fields.type")}
                      infoKey={isAudit ? "internal_audit" : "management_review"}
                    />
                  </span>
                  <div className="rounded-[14px] border-[1.5px] border-slate-200 bg-slate-50 px-4 py-3 text-[15px] font-semibold text-slate-800">
                    {t(`hse.evaluation.types.${evaluationType}`)}
                  </div>
                </div>
                <label>
                  <span className="mb-2 block text-[13px] font-bold text-slate-700">
                    {t("hse.evaluation.fields.status")}
                  </span>
                  <select
                    className={inputClass}
                    value={form.status}
                    onChange={(event) => setField("status", event.target.value)}
                  >
                    {EVALUATION_STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {t(`hse.evaluation.statuses.${status}`)}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span className="mb-2 block text-[13px] font-bold text-slate-700">
                    {t("hse.evaluation.fields.result")}
                  </span>
                  <select
                    className={inputClass}
                    value={form.result}
                    onChange={(event) => setField("result", event.target.value)}
                  >
                    <option value="">{t("hse.evaluation.results.none")}</option>
                    {EVALUATION_RESULTS.map((result) => (
                      <option key={result} value={result}>
                        {t(`hse.evaluation.results.${result}`)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="sm:col-span-2">
                  <span className="mb-2 block text-[13px] font-bold text-slate-700">
                    {t("hse.evaluation.fields.scope")}
                  </span>
                  <textarea
                    rows={3}
                    className={inputClass}
                    value={form.scope}
                    onChange={(event) => setField("scope", event.target.value)}
                  />
                </label>
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-5">
              <h3 className="mb-4 text-[16px] font-extrabold text-slate-900">
                {t("hse.evaluation.form.section.dates")}
              </h3>
              <div className="grid gap-5 sm:grid-cols-3">
                <label>
                  <span className="mb-2 block text-[13px] font-bold text-slate-700">
                    {t("hse.evaluation.fields.scheduled_on")}
                  </span>
                  <input
                    type="date"
                    className={inputClass}
                    value={form.scheduled_on}
                    onChange={(event) => setField("scheduled_on", event.target.value)}
                  />
                </label>
                <label>
                  <span className="mb-2 block text-[13px] font-bold text-slate-700">
                    {t("hse.evaluation.fields.held_on")}
                  </span>
                  <input
                    type="date"
                    className={inputClass}
                    value={form.held_on}
                    onChange={(event) => setField("held_on", event.target.value)}
                  />
                </label>
                <label>
                  <span className="mb-2 block text-[13px] font-bold text-slate-700">
                    {t("hse.evaluation.fields.next_review_on")}
                  </span>
                  <input
                    type="date"
                    className={inputClass}
                    value={form.next_review_on}
                    onChange={(event) => setField("next_review_on", event.target.value)}
                  />
                </label>
                <label>
                  <span className="mb-2 block text-[13px] font-bold text-slate-700">
                    {t("hse.evaluation.fields.period_from")}
                  </span>
                  <input
                    type="date"
                    className={inputClass}
                    value={form.period_from}
                    onChange={(event) => setField("period_from", event.target.value)}
                  />
                </label>
                <label>
                  <span className="mb-2 block text-[13px] font-bold text-slate-700">
                    {t("hse.evaluation.fields.period_to")}
                  </span>
                  <input
                    type="date"
                    className={inputClass}
                    value={form.period_to}
                    onChange={(event) => setField("period_to", event.target.value)}
                  />
                </label>
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-5">
              <h3 className="mb-4 text-[16px] font-extrabold text-slate-900">
                {t("hse.evaluation.form.section.people")}
              </h3>
              <div>
                <span className="mb-2 block text-[13px] font-bold text-slate-700">
                  {t("hse.evaluation.fields.responsible")}
                </span>
                <button
                  type="button"
                  onClick={() => setLeadPickerOpen(true)}
                  className={`${inputClass} flex items-center gap-3 text-left`}
                >
                  <span
                    className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-extrabold text-white"
                    style={{ background: avatarColor(form.responsible_user_id || "lead") }}
                  >
                    {initialsOf(personLabel(lead) || "?")}
                  </span>
                  {lead ? personLabel(lead) : t("hse.evaluation.participants.choose_one")}
                </button>
                <EmployeePickerSheet
                  open={leadPickerOpen}
                  onClose={() => setLeadPickerOpen(false)}
                  selectedId={form.responsible_user_id}
                  onSelect={(user) => setField("responsible_user_id", user?.id || "")}
                />
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-5">
              <h3 className="mb-4 text-[16px] font-extrabold text-slate-900">
                {t("hse.evaluation.form.section.conclusion")}
              </h3>
              <label className="block">
                <span className="mb-2 block text-[13px] font-bold text-slate-700">
                  {t("hse.evaluation.fields.summary")}
                </span>
                <textarea
                  rows={3}
                  className={inputClass}
                  value={form.summary}
                  onChange={(event) => setField("summary", event.target.value)}
                />
              </label>
              <label className="mt-4 block">
                <span className="mb-2 block text-[13px] font-bold text-slate-700">
                  <EvaluationLabelInfo label={t("hse.evaluation.findings.title")} infoKey="finding" />
                </span>
                <textarea
                  rows={4}
                  className={inputClass}
                  value={form.findings}
                  onChange={(event) => setField("findings", event.target.value)}
                />
              </label>
              <label className="mt-4 block">
                <span className="mb-2 block text-[13px] font-bold text-slate-700">
                  {t("hse.evaluation.fields.conclusions")}
                </span>
                <textarea
                  rows={4}
                  className={inputClass}
                  value={form.conclusions}
                  onChange={(event) => setField("conclusions", event.target.value)}
                />
              </label>
            </section>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => navigate(listPath)}
                className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-700"
              >
                {t("hse.evaluation.form.cancel")}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="rounded-full bg-[#1463C7] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_12px_24px_-8px_rgba(20,99,199,.6)] disabled:opacity-60"
              >
                {saving ? t("hse.evaluation.form.saving") : t("hse.evaluation.form.save")}
              </button>
            </div>
          </form>
        )}
      </div>
    </HsePageChrome>
  );
}
